import { PointOfInterestType } from '@/models/enums';

import type { FcwMapMarkerProps } from '@/components/atoms/FcwMapMarker';

export type StationMarkerType =
  | 'station'
  | 'station-selected'
  | 'station-closed'
  | 'station-closed-selected';

export function getStationMarkerProps(
  station: MapStation,
  isSelected: boolean,
  theme: ClientState['theme']
): FcwMapMarkerProps {
  const markerType: StationMarkerType = (() => {
    switch (true) {
      case station.isClosed && isSelected:
        return 'station-closed-selected';
      case station.isClosed && !isSelected:
        return 'station-closed';
      case !station.isClosed && isSelected:
        return 'station-selected';
      default:
        return 'station';
    }
  })();

  const value = station.availableBikes ?? 0;
  const isEmpty = station.availableBikes === 0;
  const progress =
    (station.availableBikes /
      (station.availableBikes + station.availableSlots)) *
    100;
  const color = isEmpty ? theme.colorPrimaryLight1 : theme.colorPrimary;
  const shape = station.isVirtual ? 'squared' : 'rounded';

  switch (markerType) {
    case 'station':
      return {
        type: 'numeric',
        value,
        progress,
        color,
        shape,
      };
    case 'station-selected':
      return {
        type: 'numeric',
        value,
        progress,
        color,
        shape,
        selected: true,
      };
    case 'station-closed':
      return {
        type: 'icon',
        icon: 'forbidden',
        progress: 100,
        color: theme.colorNeutralDark3,
        shape,
      };
    case 'station-closed-selected':
      return {
        type: 'icon',
        icon: 'forbidden',
        progress: 100,
        color: theme.colorNeutralDark3,
        shape,
        selected: true,
      };
  }
}

type PointOfInterestMarkerType =
  | 'bike-locker'
  | 'bike-locker-selected'
  | 'bike-house'
  | 'bike-house-selected'
  | 'unknown'
  | 'unknown-selected';

export function getPointOfInterestMarkerProps(
  pointOfInterest: MapPointOfInterest,
  theme: ClientState['theme'],
  isSelected = false
): FcwMapMarkerProps {
  const markerType: PointOfInterestMarkerType = (() => {
    switch (true) {
      case pointOfInterest.type === PointOfInterestType.BikeLocker &&
        !isSelected:
        return 'bike-locker';
      case pointOfInterest.type === PointOfInterestType.BikeLocker &&
        isSelected:
        return 'bike-locker-selected';
      case pointOfInterest.type === PointOfInterestType.BikeHouse &&
        !isSelected:
        return 'bike-house';
      case pointOfInterest.type === PointOfInterestType.BikeHouse && isSelected:
        return 'bike-house-selected';
      case pointOfInterest.type === PointOfInterestType.Unknown && !isSelected:
      default:
        return 'unknown';
      case pointOfInterest.type === PointOfInterestType.Unknown && isSelected:
        return 'unknown-selected';
    }
  })();

  switch (markerType) {
    case 'bike-house':
      return {
        type: 'icon',
        color: theme.colorSecondaryLight1,
        icon: 'bike-house',
        shape: 'rounded',
        progress: 100,
      };
    case 'bike-house-selected':
      return {
        type: 'icon',
        color: theme.colorSecondaryLight1,
        icon: 'bike-house',
        shape: 'rounded',
        progress: 100,
        selected: true,
      };
    case 'bike-locker':
      return {
        type: 'icon',
        color: theme.colorSecondary,
        icon: 'bike-locker',
        shape: 'rounded',
        progress: 100,
      };
    case 'bike-locker-selected':
      return {
        type: 'icon',
        color: theme.colorSecondary,
        icon: 'bike-locker',
        shape: 'rounded',
        progress: 100,
        selected: true,
      };
    case 'unknown':
      return {
        type: 'icon',
        color: theme.colorSecondary,
        icon: 'info',
        shape: 'rounded',
        progress: 100,
      };
    case 'unknown-selected':
      return {
        type: 'icon',
        color: theme.colorSecondary,
        icon: 'info',
        shape: 'rounded',
        progress: 100,
        selected: true,
      };
  }
}
